import React from 'react';
import { toast } from 'react-toastify';
import type { FieldArrayMethodProps } from 'react-hook-form';

import { ProductArrangementEntry, SelectedProductArrangement } from './ProductPriceRangeCreate';

import { TotemSelect } from 'components/TotemSelect';
import { Loader, LoaderModeType } from 'components/Loader';
import { useGetProductArrangementsQuery } from 'data/__generated__';

export const PriceRangeProductArrangementSelector = ({
    append,
    selectedProductArrangements,
    setSelectedProductArrangements,
    shouldDisplaySelect = true,
}: {
    append: (
        value: ProductArrangementEntry | ProductArrangementEntry[],
        options?: FieldArrayMethodProps | undefined,
    ) => void;
    selectedProductArrangements: SelectedProductArrangement[];
    setSelectedProductArrangements: React.Dispatch<React.SetStateAction<SelectedProductArrangement[]>>;
    shouldDisplaySelect?: boolean;
}) => {
    const {
        loading: productArrangementsLoading,
        data: productArrangementsData,
        error: productArrangementsError,
    } = useGetProductArrangementsQuery();

    if (productArrangementsLoading) {
        return <Loader mode={LoaderModeType.Spin} />;
    }

    if (productArrangementsError || !productArrangementsData) {
        toast.error('Une erreur est survenue lors de la récupération des produits non-archivés ');
        return null;
    }

    const { productArrangementsQuery: productArrangements } = productArrangementsData;

    const selectedProductArrangementIds = selectedProductArrangements.map(
        ({ productArrangementId }) => productArrangementId,
    );

    const productArrangementsOptions =
        productArrangements
            .filter(({ _id }) => !selectedProductArrangementIds.includes(_id))
            .map(({ _id, name }) => ({
                value: _id,
                label: name,
            })) || [];

    const onSelect = (value: string | null) => {
        if (!value) {
            return;
        }
        const option = productArrangementsOptions.find((o) => o.value === value);
        if (option && !selectedProductArrangements.some(({ productArrangementId }) => productArrangementId === value)) {
            setSelectedProductArrangements([
                ...selectedProductArrangements,
                { productArrangementId: value, name: option.label },
            ]);
            append({ productArrangementId: value } as ProductArrangementEntry);
        }
    };

    return shouldDisplaySelect ? (
        <TotemSelect
            dataTest="select-product-arrangement"
            label="Ajouter une gamme de produit"
            placeholder="Sélectionner une gamme de produit (recherche par nom ou par _id)"
            value={null}
            options={productArrangementsOptions}
            onChange={onSelect}
        />
    ) : (
        <></>
    );
};
